
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/auth-context';
import { hasAccess } from '../config/tier-config';
import {
  ALL_STRATEGIES,
  getAlertSettings,
  saveAlertSettings,
  generateLinkToken,
  disconnectTelegram,
  onAlertSettingsChange,
} from '../services/alert-settings-service';
import './settings-page.css';

const DEV_TIERS = ['free', 'basic', 'pro', 'elite'];

function SettingsPage() {
  const { user, tier, setTierOverride } = useAuth();
  const isDevAdmin = !!(
    process.env.REACT_APP_DEV_ADMIN_EMAIL &&
    user?.email === process.env.REACT_APP_DEV_ADMIN_EMAIL
  );
  const [tierSwitching, setTierSwitching] = useState(null); // tier key currently being applied
  const [tierSwitchError, setTierSwitchError] = useState(null);

  // ── Billing (Stripe portal) state ───────────────────────────────────────────
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError]     = useState(null);

  // ── Telegram alert state ────────────────────────────────────────────────────
  const [alertSettings, setAlertSettings]     = useState(null);      // loaded from Firestore
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [connectLink, setConnectLink]         = useState(null);       // deep link shown after clicking Connect
  const [alertSaving, setAlertSaving]         = useState(false);
  const [alertMsg, setAlertMsg]               = useState(null);
  const [telegramListenerError, setTelegramListenerError] = useState(null);

  const canUseAlerts = hasAccess(tier, 'basic');
  const botUsername  = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';

  // Load alert settings + subscribe to real-time chatId updates
  useEffect(() => {
    if (!user) return;
    getAlertSettings(user.uid).then(s => {
      if (s) {
        setAlertSettings(s);
        setSelectedStrategies(s.strategies || []);
        setTelegramEnabled(s.telegramEnabled || false);
      }
    });
    const unsub = onAlertSettingsChange(
      user.uid,
      s => {
        setTelegramListenerError(null);
        if (s) {
          setAlertSettings(s);
          setSelectedStrategies(s.strategies || []);
          setTelegramEnabled(s.telegramEnabled || false);
          // If chatId just arrived, clear the connect link and confirm connection
          if (s.chatId) setConnectLink(null);
        }
      },
      err => setTelegramListenerError('Could not load live status: ' + err.message)
    );
    return unsub;
  }, [user]);

  const handleStrategyToggle = (key) => {
    setSelectedStrategies(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSaveAlertSettings = async () => {
    if (!user) return;
    setAlertSaving(true);
    try {
      await saveAlertSettings(user.uid, {
        telegramEnabled,
        strategies: selectedStrategies,
      });
      setAlertMsg('Alert settings saved.');
      setTimeout(() => setAlertMsg(null), 3000);
    } catch (err) {
      setAlertMsg('Failed to save: ' + err.message);
    } finally {
      setAlertSaving(false);
    }
  };

  const handleConnectTelegram = async () => {
    if (!user || !botUsername) return;
    const token = await generateLinkToken(user.uid);
    setConnectLink(`https://t.me/${botUsername}?start=${token}`);
  };

  const handleDisconnect = async () => {
    if (!user) return;
    await disconnectTelegram(user.uid);
    setAlertSettings(prev => ({ ...prev, chatId: null, telegramEnabled: false }));
    setTelegramEnabled(false);
    setConnectLink(null);
  };

  const isConnected = !!(alertSettings?.chatId);

  const handleManageBilling = async () => {
    if (!user) return;
    setPortalLoading(true);
    setPortalError(null);
    try {
      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to open billing portal');
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setPortalError(err.message);
      setPortalLoading(false);
    }
  };

  const handleDevTierSwitch = async (newTier) => {
    if (newTier === tier) return;
    setTierSwitching(newTier);
    setTierSwitchError(null);
    try {
      await setTierOverride(newTier);
    } catch (err) {
      setTierSwitchError(err.message);
    } finally {
      setTierSwitching(null);
    }
  };

  return (
    <div className="settings-page">
      {/* Page Header */}
      <div className="page-header">
        <h1>Settings</h1>
        <p className="page-subtitle">Configure your CryptoProScanner experience</p>
      </div>

      {/* Settings Sections */}
      <div className="settings-container">

        {/* Dev-only tier switcher — REACT_APP_DEV_ADMIN_EMAIL must match the logged-in user */}
        {isDevAdmin && (
          <div className="settings-section dev-tier-section">
            <h3 className="section-title">Dev: Tier Override</h3>
            <p className="setting-description">
              Writes directly to your Firestore user doc — bypasses Stripe entirely.
              Visible only to you.
            </p>
            <div className="dev-tier-buttons">
              {DEV_TIERS.map(t => (
                <button
                  key={t}
                  type="button"
                  className={`dev-tier-btn ${tier === t ? 'active' : ''}`}
                  onClick={() => handleDevTierSwitch(t)}
                  disabled={tierSwitching !== null}
                >
                  {tierSwitching === t ? '…' : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            {tierSwitchError && <div className="dev-tier-error">{tierSwitchError}</div>}
          </div>
        )}

        {/* Billing */}
        {tier !== 'free' && (
          <div className="settings-section">
            <h3 className="section-title">Billing</h3>
            <p className="setting-description">
              Update your payment method, change plans, or cancel your subscription.
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleManageBilling}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening...' : 'Manage billing'}
            </button>
            {portalError && <div className="dev-tier-error">{portalError}</div>}
          </div>
        )}

        {/* Telegram Alerts */}
        <div className="settings-section">
          <h3 className="section-title">Telegram Alerts</h3>

          {telegramListenerError && (
            <div className="dev-tier-error">{telegramListenerError}</div>
          )}

          {!canUseAlerts ? (
            <div className="telegram-locked">
              <p className="setting-description">
                Get instant alerts sent straight to Telegram when your strategies fire a signal.
                Available on the Basic plan and above.
              </p>
              <a href="/pricing?upgrade=basic" className="btn-primary upgrade-btn">
                Upgrade to unlock
              </a>
            </div>
          ) : (
            <>
              <div className="telegram-connect">
                {isConnected ? (
                  <div className="telegram-connect-row">
                    <span className="telegram-status connected">&#9989; Connected to Telegram</span>
                    <button type="button" className="btn-secondary" onClick={handleDisconnect}>
                      Disconnect
                    </button>
                  </div>
                ) : connectLink ? (
                  <div className="telegram-connect-link">
                    <p className="setting-description">
                      Tap this link on your phone to connect your Telegram account. It expires in 24 hours.
                    </p>
                    <a href={connectLink} target="_blank" rel="noreferrer" className="telegram-deep-link">
                      {connectLink}
                    </a>
                    <div className="telegram-pending">
                      <span className="saving-spinner">⟳</span>
                      Waiting for confirmation — tap the link above in Telegram and press Start
                    </div>
                  </div>
                ) : (
                  <div className="telegram-connect-row">
                    <span className="setting-description">
                      Connect your Telegram account to start receiving alerts.
                    </span>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleConnectTelegram}
                      disabled={!botUsername}
                      title={!botUsername ? 'Telegram bot is not configured yet' : undefined}
                    >
                      Connect Telegram
                    </button>
                  </div>
                )}
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="telegram-enabled">Enable Telegram Alerts</label>
                  <span className="setting-description">
                    {isConnected
                      ? 'Turn alert delivery on or off without disconnecting your account'
                      : 'Connect your Telegram account above first'}
                  </span>
                </div>
                <div className="setting-control">
                  <label
                    className="toggle-switch"
                    title={!isConnected ? 'Connect your Telegram account first' : undefined}
                  >
                    <input
                      id="telegram-enabled"
                      type="checkbox"
                      checked={telegramEnabled}
                      onChange={(e) => setTelegramEnabled(e.target.checked)}
                      disabled={!isConnected}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="telegram-strategies">
                <span className="setting-description telegram-strategies-label">
                  Choose which strategies send you alerts
                </span>
                <div className="strategy-checkbox-list">
                  {ALL_STRATEGIES.filter(s => hasAccess(tier, s.tier)).map(s => {
                    return (
                      <label key={s.key} className="strategy-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedStrategies.includes(s.key)}
                          onChange={() => handleStrategyToggle(s.key)}
                        />
                        <span>{s.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {alertMsg && <div className="telegram-alert-msg">{alertMsg}</div>}

              <button
                type="button"
                className={`btn-primary save-btn telegram-save-btn ${alertSaving ? 'saving' : ''}`}
                onClick={handleSaveAlertSettings}
                disabled={alertSaving}
              >
                {alertSaving ? (
                  <>
                    <span className="saving-spinner">⟳</span>
                    Saving...
                  </>
                ) : (
                  'Save Alert Settings'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
